import React, { useState } from "react";
import { motion } from "framer-motion";
import { Gift, Building2, Package, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const types = [
  { id: "gift", label: "Personal Gifting", icon: Gift, desc: "Curated hampers for birthdays, anniversaries & festivals." },
  { id: "bulk", label: "Bulk Orders", icon: Package, desc: "Large quantities for weddings and celebrations." },
  { id: "corporate", label: "Corporate Gifting", icon: Building2, desc: "Custom-branded boxes for clients & teams." },
];

export default function GiftCorporate() {
  const [type, setType] = useState("gift");
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", quantity: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!form.name || !form.email || !form.phone) return toast.error("Please fill name, email and phone");
    setLoading(true);
    try {
      await api.post("/gift-requests", { type, ...form });
      toast.success("Request received! Our concierge will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", quantity: "", message: "" });
    } catch (e) {
      toast.error("Could not submit. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="gifting-page">
      <section className="relative py-24 overflow-hidden">
        <img src="/catalogue/decor/gift-box.webp" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#120A07]/85" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <span className="eyebrow">Bespoke Gifting Concierge</span>
          <h1 className="font-display text-4xl sm:text-6xl mt-3">Gift, Bulk & Corporate</h1>
          <p className="text-[#C8B8AB] mt-5 max-w-2xl mx-auto">From custom-branded corporate hampers to large festival orders — our concierge crafts the perfect chocolate experience for every occasion.</p>
          <a href="/gifting-guide" data-testid="gifting-guide-link" className="btn-outline-gold inline-block px-6 py-3 rounded-full mt-6 text-sm">Browse the Gifting Occasion Guide</a>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-16">
        <div className="grid sm:grid-cols-3 gap-5 mb-10">
          {types.map((t) => (
            <button key={t.id} onClick={() => setType(t.id)} data-testid={`gift-type-${t.id}`}
              className={`lux-card p-6 text-left transition hover-lift ${type === t.id ? "border-[#D4AF37]" : ""}`}>
              <div className={`h-12 w-12 rounded-full flex items-center justify-center mb-4 ${type === t.id ? "bg-[#D4AF37] text-[#1A0F0B]" : "bg-[#D4AF37]/10 text-[#D4AF37]"}`}><t.icon size={22} /></div>
              <h3 className="font-display text-lg">{t.label}</h3>
              <p className="text-[#8C7B6E] text-sm mt-1">{t.desc}</p>
            </button>
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lux-card p-8">
          <h3 className="font-display text-2xl mb-6">Request a Quote</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input data-testid="gift-name" placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7]" />
            <Input data-testid="gift-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7]" />
            <Input data-testid="gift-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7]" />
            <Input data-testid="gift-company" placeholder="Company (optional)" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7]" />
            <Input data-testid="gift-quantity" placeholder="Estimated quantity" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7] sm:col-span-2" />
            <Textarea data-testid="gift-message" placeholder="Tell us about your requirement…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7] sm:col-span-2" />
          </div>
          <Button data-testid="gift-submit-btn" onClick={submit} disabled={loading} className="btn-gold rounded-full mt-6 px-8 h-12">
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Submit Request"}
          </Button>
        </motion.div>
      </section>
    </div>
  );
}
